import React, { useCallback, useImperativeHandle } from 'react';
import { useLocalStorage } from 'react-use';
import { useImmer } from 'use-immer';
import { OrderDetailModal, OrderDetailModalProps } from '../../pages/Order/DetailModal';
import {
  STORAGE_KEY_COMMAND_HISTORY,
  STORAGE_KEY_COMMAND_MAX_ITEMS,
} from '@/utils/storage';
import { getZIndex } from '../Modal';
import { SelectOperatorModal, SelectOperatorModalProps } from '../OperatorPosModalSelector';
import { BindOrderQRCodeModal, BindOrderQRCodeModalProps } from '../OrderDescriptions/BindOrder';
import UserModal, { UserModalProps } from '../UserModal';

/**
 * 支持的指令历史类型
 */
export enum CommandEntityTypeEnum {
  ORDER,
  USER,
}

type CommandEntityPropsMap = {
  [CommandEntityTypeEnum.ORDER]: OrderDetailModalProps;
  [CommandEntityTypeEnum.USER]: UserModalProps;
};


export const STORAGE_KEY_COMMAND_INIT_VALUE = {
  // [CommandEntityTypeEnum.ORDER]: {},
  // [CommandEntityTypeEnum.USER]: {},
  [CommandEntityTypeEnum.ORDER]: [],
  [CommandEntityTypeEnum.USER]: [],
};
export type STORAGE_KEY_COMMAND_HISTORY_TYPE = {
  // [type: string]: { [key: string]: CommandHistroy & { _props: object } };
  [type: string]: (CommandHistroy & { _props: object })[];
};

/**
 * 指令内部实体数据就绪回调
 * 从实体内部发出信息，告知外部实体的基础信息
 * 用于指令历史记录，例如用户弹窗、订单弹窗
 */
export type CommandHistroy<T extends CommandEntityTypeEnum = CommandEntityTypeEnum> = {
  key: string;
  title: string;
  img?: string;
  subTitle?: string;
  /**业务可添加覆盖保存到 storage 里的 props */
  props?: CommandEntityPropsMap[T];
};
export type CommandEntityReady = {
  onCommandEntityReady?: <T extends CommandEntityTypeEnum>(args: { type: T } & CommandHistroy<T>) => void;
};

export interface CommanderResponseRef {
  showOrderModal: (orderProps: OrderDetailModalProps) => void;
  showAppointmentModal: (orderProps: OrderDetailModalProps) => void;
  showUserModal: (userProps: UserModalProps) => void;
  showBindOrderModal: (bindOrderProps: BindOrderQRCodeModalProps) => void;
  showSelectOperatorModal: (selectOperatorProps: SelectOperatorModalProps) => void;
  closeUserModal: () => void;
  closeAppointmentModal: () => void;
  closeOrderModal: () => void;
  closeSelectOperatorModal: () => void;
}
export const CommanderResponse = React.forwardRef<CommanderResponseRef>((props, ref) => {
  const [, setCommandHistory] = useLocalStorage<STORAGE_KEY_COMMAND_HISTORY_TYPE>(
    STORAGE_KEY_COMMAND_HISTORY,
    STORAGE_KEY_COMMAND_INIT_VALUE
  );

  const [params, setParams] = useImmer<
    Partial<{
      orderProps: OrderDetailModalProps;
      userProps: UserModalProps;
      bindOrderProps: BindOrderQRCodeModalProps;
      selectOperatorProps: SelectOperatorModalProps;
    }>
  >({});

  const { orderProps, userProps, bindOrderProps, selectOperatorProps } = params;

  useImperativeHandle(ref, () => {
    return {
      showAppointmentModal: (orderProps: OrderDetailModalProps) => {
        setParams(draft => {
          draft.orderProps = { ...orderProps, zIndex: getZIndex() };
        });
      },
      showOrderModal: (orderProps: OrderDetailModalProps) => {
        setParams(draft => {
          draft.orderProps = { ...orderProps, zIndex: getZIndex() };
        });
      },
      showUserModal: (userProps: UserModalProps) => {
        setParams(draft => {
          draft.userProps = { ...userProps, zIndex: getZIndex() };
        });
      },
      showBindOrderModal: (bindOrderProps: BindOrderQRCodeModalProps) => {
        setParams(draft => {
          draft.bindOrderProps = { ...bindOrderProps, zIndex: getZIndex() };
        });
      },
      showSelectOperatorModal: (selectOperatorProps: SelectOperatorModalProps) => {
        setParams(draft => {
          draft.selectOperatorProps = { ...selectOperatorProps, zIndex: getZIndex() };
        });
      },
      closeAppointmentModal: () =>
        setParams(draft => {
          draft.orderProps = null;
        }),
      closeUserModal: () =>
        setParams(draft => {
          draft.userProps = null;
        }),
      closeOrderModal: () =>
        setParams(draft => {
          draft.orderProps = null;
        }),
      closeSelectOperatorModal: () =>
        setParams(draft => {
          draft.selectOperatorProps = null;
        }),
    };
  }, [setParams]);

  // 保存到历史记录
  const onCommandEntityReady = useCallback<CommandEntityReady['onCommandEntityReady']>(
    args => {
      setCommandHistory(commandHistory => {
        const { type, props = {} } = args;
        delete args.type;
        delete args.props;
        // delete args.key;

        let _props: CommandEntityPropsMap[typeof type];
        if (type === CommandEntityTypeEnum.ORDER) {
          _props = Object.assign({}, orderProps, props);
        } else if (type === CommandEntityTypeEnum.USER) {
          _props = Object.assign({}, userProps, props);
        }

        if (!_props) return commandHistory;

        delete _props.zIndex;

        // 去重
        commandHistory[type] = commandHistory[type]
          .filter(item => item.key !== args.key)
          .slice(0, STORAGE_KEY_COMMAND_MAX_ITEMS - 1);
        // 添加新记录
        commandHistory[type].unshift({ ...args, _props });

        return commandHistory;
      });
    },
    [userProps, orderProps, setCommandHistory]
  );

  return (
    <>
      {orderProps ? (
        <OrderDetailModal
          {...orderProps}
          onClose={(...args) => {
            orderProps.onClose?.(...args);
            setParams(draft => {
              draft.orderProps = null;
            });
          }}
          onCommandEntityReady={onCommandEntityReady}
        />
      ) : null}
      {userProps ? (
        <UserModal
          {...userProps}
          onClose={(...args) => {
            userProps.onClose?.(...args);
            setParams(draft => {
              draft.userProps = null;
            });
          }}
          onCommandEntityReady={onCommandEntityReady}
        />
      ) : null}

      {bindOrderProps ? (
        <BindOrderQRCodeModal
          {...bindOrderProps}
          onClose={() => {
            setParams(draft => {
              draft.bindOrderProps = null;
            });
          }}
        />
      ) : null}

      {selectOperatorProps ? (
        <SelectOperatorModal
          {...selectOperatorProps}
          onClose={() => {
            setParams(draft => {
              draft.selectOperatorProps = null;
            });
          }}
        />
      ) : null}
    </>
  );
});
